import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Plus, X } from 'lucide-react';
import {
  loadDistricts,
  loadProvinceSettlements,
  searchItems,
} from '../../lib/locationData';

const DEFAULT_PROVINCE_ID = 17;
const DEFAULT_PROVINCE = {
  id: DEFAULT_PROVINCE_ID,
  code: '17',
  name: 'Çanakkale',
};

function byTurkishName(a, b) {
  if (a.name === 'Merkez') return -1;
  if (b.name === 'Merkez') return 1;
  return a.name.localeCompare(b.name, 'tr');
}

function bySettlementGroup(a, b) {
  if (a.groupOrder !== b.groupOrder) return a.groupOrder - b.groupOrder;
  return (a.name || a.label || '').localeCompare((b.name || b.label || ''), 'tr');
}

function settlementGroup(item) {
  if (item.type === 'neighborhood' || item.id === 'municipality-kepez') {
    return { groupLabel: 'Mahalleler', groupOrder: 0 };
  }
  return { groupLabel: 'Köyler ve Beldeler', groupOrder: 1 };
}

function selectionKey(selection) {
  return [selection.scope, selection.province_code || selection.provinceCode, selection.district_code || selection.districtCode, selection.settlement_code || selection.settlementCode]
    .filter(Boolean)
    .join(':');
}

function formatRawLocation(location) {
  if (!location) return '';
  if (location.raw) return location.raw;
  return [location.province, location.district, location.neighborhood].filter(Boolean).join(' / ');
}

function buildSingleLocation(province, district, settlement) {
  const selectedProvince = province || DEFAULT_PROVINCE;
  const base = {
    mode: 'single',
    province: selectedProvince.name,
    province_code: selectedProvince.code || String(selectedProvince.id).padStart(2, '0'),
    district: district?.name || '',
    district_code: district?.id ? String(district.id) : '',
    districts: district?.name ? [district.name] : [],
    neighborhood: settlement?.name || '',
    municipality: settlement?.municipalityName || '',
    municipality_code: settlement?.municipalityId ? String(settlement.municipalityId) : '',
    settlement: settlement?.name || '',
    settlement_type: settlement?.type || '',
    settlement_code: settlement?.id || '',
    raw: [selectedProvince.name, district?.name, settlement?.label].filter(Boolean).join(' / '),
    selections: [],
  };

  if (settlement) {
    base.selections = [{
      scope: 'settlement',
      province: selectedProvince.name,
      province_code: base.province_code,
      district: district?.name || settlement.districtName || '',
      district_code: district?.id ? String(district.id) : String(settlement.districtId || ''),
      municipality: settlement.municipalityName || '',
      municipality_code: settlement.municipalityId ? String(settlement.municipalityId) : '',
      settlement: settlement.name,
      settlement_code: settlement.id,
      settlement_type: settlement.type,
      label: settlement.label,
    }];
  }

  return base;
}

function buildMultipleLocation(province, selections) {
  const selectedProvince = province || DEFAULT_PROVINCE;
  const labels = selections.map((item) => item.label).filter(Boolean);
  const districts = [...new Set(selections.map((item) => item.district).filter(Boolean))];
  const firstSettlement = selections.find((item) => item.scope === 'settlement');

  return {
    mode: 'multiple',
    province: selectedProvince.name,
    province_code: selectedProvince.code || String(selectedProvince.id).padStart(2, '0'),
    district: districts.length === 1 ? districts[0] : '',
    district_code: districts.length === 1 ? selections.find((item) => item.district === districts[0])?.district_code || '' : '',
    districts,
    neighborhood: firstSettlement?.settlement || '',
    municipality: firstSettlement?.municipality || '',
    municipality_code: firstSettlement?.municipality_code || '',
    settlement: firstSettlement?.settlement || '',
    settlement_type: firstSettlement?.settlement_type || '',
    settlement_code: firstSettlement?.settlement_code || '',
    raw: labels.join(' | '),
    selections,
  };
}

function SearchableSelect({
  label,
  valueLabel,
  placeholder,
  items,
  onSelect,
  disabled,
  searchFields,
}) {
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);
  const filtered = useMemo(() => searchItems(items, query, searchFields), [items, query, searchFields]);

  useEffect(() => {
    const close = (event) => {
      if (!wrapRef.current?.contains(event.target)) setOpen(false);
    };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, []);

  const choose = (item) => {
    onSelect(item);
    setQuery('');
    setOpen(false);
  };

  return (
    <div className="ckr-location-combo" ref={wrapRef}>
      <label className="ckr-label">{label}</label>
      <button
        type="button"
        className="ckr-location-control"
        aria-label={label}
        aria-expanded={open}
        onClick={() => !disabled && setOpen((current) => !current)}
        disabled={disabled}
      >
        <span>{valueLabel || placeholder}</span>
      </button>
      {open && !disabled && (
        <div className="ckr-location-popover">
          <input
            className="ckr-input"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder="Ara..."
            autoFocus
          />
          <div className="ckr-location-options">
            {filtered.map((item, index) => (
              <React.Fragment key={item.id}>
                {item.groupLabel && item.groupLabel !== filtered[index - 1]?.groupLabel && (
                  <div className="ckr-location-group-label">{item.groupLabel}</div>
                )}
                <button type="button" onClick={() => choose(item)}>
                  <strong>{item.label || item.name}</strong>
                  {item.meta && <span>{item.meta}</span>}
                </button>
              </React.Fragment>
            ))}
            {filtered.length === 0 && <p>Sonuç bulunamadı.</p>}
          </div>
        </div>
      )}
    </div>
  );
}

function SelectionTags({ selections, onRemove }) {
  if (!selections.length) return null;
  return (
    <div className="ckr-location-tags">
      {selections.map((selection) => (
        <span className="ckr-location-tag" key={selectionKey(selection)}>
          {selection.label}
          <button type="button" onClick={() => onRemove(selectionKey(selection))} aria-label={`${selection.label} seçimini kaldır`}>
            <X size={13} />
          </button>
        </span>
      ))}
    </div>
  );
}

export default function LocationSelector({ mode = 'single', value, onChange, title }) {
  const [allDistricts, setAllDistricts] = useState([]);
  const [settlements, setSettlements] = useState([]);
  const [loading, setLoading] = useState(true);
  const settlementLoading = loading;
  const [dataError, setDataError] = useState('');
  const province = DEFAULT_PROVINCE;
  const initialLocation = useRef(value);
  const [district, setDistrict] = useState(null);
  const [settlement, setSettlement] = useState(null);
  const [districtFilter, setDistrictFilter] = useState(null);
  const [multiSelections, setMultiSelections] = useState([]);

  const isMulti = mode === 'multiple';

  useEffect(() => {
    let mounted = true;
    Promise.all([loadDistricts(), loadProvinceSettlements(DEFAULT_PROVINCE_ID)])
      .then(([districtRows, settlementRows]) => {
        if (!mounted) return;
        setAllDistricts(districtRows);
        setSettlements(settlementRows);
        const initial = initialLocation.current || {};
        const supported = (!initial.province_code || String(initial.province_code) === '17') && (!initial.province || initial.province === 'Çanakkale');
        if (supported) {
          const code = initial.district_code || initial.selections?.[0]?.district_code;
          const selectedDistrict = districtRows.find(item => item.provinceId === DEFAULT_PROVINCE_ID && (String(item.id) === String(code) || item.name === initial.district));
          setDistrict(selectedDistrict || null);
          setDistrictFilter(selectedDistrict || null);
          let selectedSettlement = settlementRows.find(item => item.districtId === selectedDistrict?.id && (item.id === initial.settlement_code || item.name === (initial.settlement || initial.neighborhood)));
          if (!selectedSettlement && initial.settlement_code === 'municipality-kepez' && selectedDistrict?.id === 1230) {
            const official = settlementRows.find(item => item.districtId === 1230 && item.municipalityName === 'Kepez');
            if (official) selectedSettlement = {...official, id:'municipality-kepez', name:'Kepez', label:'Kepez', type:'municipality'};
          }
          setSettlement(selectedSettlement || null);
          setMultiSelections((initial.selections || []).filter(item => (!item.province_code || String(item.province_code) === '17') && (!item.province || item.province === 'Çanakkale')));
        }
      })
      .catch(error => { if (mounted) setDataError(error.message || 'Konum verileri yüklenemedi.'); })
      .finally(() => { if (mounted) setLoading(false); });
    return () => { mounted = false; };
  }, []);

  const districtOptions = useMemo(() => (
    allDistricts
      .filter((item) => item.provinceId === province?.id)
      .sort(byTurkishName)
      .map((item) => ({
        ...item,
        label: item.name,
        meta: `${item.neighborhoodCount || 0} mahalle · ${item.villageCount || 0} köy`,
      }))
  ), [allDistricts, province?.id]);

  const settlementOptions = useMemo(() => {
    const selectedDistrictId = isMulti ? districtFilter?.id : district?.id;
    if (!selectedDistrictId) return [];
    const rows = settlements
      .filter((item) => item.districtId === selectedDistrictId)
      .map((item) => ({
        ...item,
        ...settlementGroup(item),
        meta: item.typeLabel,
      }));

    if (province?.id === DEFAULT_PROVINCE_ID && Number(selectedDistrictId) === 1230) {
      const kepezNeighborhood = rows.find((item) => item.municipalityName === 'Kepez');
      if (kepezNeighborhood && !rows.some((item) => item.id === 'municipality-kepez')) {
        rows.push({
          id: 'municipality-kepez',
          officialId: kepezNeighborhood.municipalityId || '1617',
          name: 'Kepez',
          label: 'Kepez',
          type: 'municipality',
          typeLabel: 'Belde',
          provinceId: DEFAULT_PROVINCE_ID,
          provinceCode: '17',
          provinceName: 'Çanakkale',
          districtId: selectedDistrictId,
          districtName: 'Merkez',
          municipalityId: kepezNeighborhood.municipalityId,
          municipalityName: 'Kepez',
          municipalityType: 'town',
          groupLabel: 'Mahalleler',
          groupOrder: 0,
          meta: 'Belde',
        });
      }
    }

    return rows.sort(bySettlementGroup);
  }, [district?.id, districtFilter?.id, isMulti, province?.id, settlements]);

  useEffect(() => {
    if (loading) return;
    const next = isMulti
      ? buildMultipleLocation(province, multiSelections)
      : buildSingleLocation(province, district, settlement);
    if (formatRawLocation(next) !== formatRawLocation(value) || JSON.stringify(next.selections || []) !== JSON.stringify(value?.selections || [])) {
      onChange(next);
    }
  }, [district, isMulti, loading, multiSelections, onChange, province, settlement, value]);

  const chooseDistrict = (item) => {
    setDistrict(item);
    setSettlement(null);
  };

  const addSelection = (selection) => {
    setMultiSelections((current) => {
      const withoutConflicts = current.filter((item) => {
        if (selection.scope === 'province_any') return false;
        if (item.scope === 'province_any') return false;
        if (selection.scope === 'district_all' && item.district_code === selection.district_code) return false;
        if (selection.scope === 'settlement' && item.scope === 'district_all' && item.district_code === selection.district_code) return false;
        return selectionKey(item) !== selectionKey(selection);
      });
      return [...withoutConflicts, selection];
    });
  };

  const addProvinceAny = () => {
    addSelection({
      scope: 'province_any',
      province: province.name,
      province_code: province.code || String(province.id).padStart(2, '0'),
      label: `${province.name} il genelinde fark etmez`,
    });
  };

  const addDistrictAll = () => {
    if (!districtFilter) return;
    addSelection({
      scope: 'district_all',
      province: province.name,
      province_code: province.code || String(province.id).padStart(2, '0'),
      district: districtFilter.name,
      district_code: String(districtFilter.id),
      label: `${districtFilter.name} ilçesinin tamamı`,
    });
  };

  const addSettlement = (item) => {
    addSelection({
      scope: 'settlement',
      province: province.name,
      province_code: province.code || String(province.id).padStart(2, '0'),
      district: item.districtName,
      district_code: String(item.districtId),
      municipality: item.municipalityName || '',
      municipality_code: item.municipalityId ? String(item.municipalityId) : '',
      settlement: item.name,
      settlement_code: item.id,
      settlement_type: item.type,
      label: `${item.districtName} / ${item.label}`,
    });
  };

  const removeSelection = (key) => {
    setMultiSelections((current) => current.filter((item) => selectionKey(item) !== key));
  };

  if (dataError) {
    return <p className="ckr-location-error">{dataError}</p>;
  }

  return (
    <div className="ckr-location-block">
      <div className="ckr-location-head">
        <h4>{title || (isMulti ? 'Hangi Bölgelerde Mülk Arıyorsunuz?' : 'Mülkünüz Nerede Bulunuyor?')}</h4>
        <p>
          {isMulti
            ? 'Çanakkale’de birden fazla ilçe, mahalle veya köy ekleyebilirsiniz.'
            : 'Çanakkale’de mülkün bulunduğu ilçe ve yerleşimi seçin.'}
        </p>
      </div>

      <div className="ckr-location-grid">
        {isMulti ? (
          <>
            <SearchableSelect
              label="Yerleşim eklemek için ilçe seç"
              valueLabel={districtFilter?.name}
              placeholder="İlçe seçin"
              items={districtOptions}
              onSelect={setDistrictFilter}
              disabled={loading || !province}
              searchFields={['name']}
            />
            <div className="ckr-location-actions">
              <button type="button" className="ckr-location-mini-btn" onClick={addProvinceAny}>
                <Plus size={14} /> İl Genelinde Fark Etmez
              </button>
              <button type="button" className="ckr-location-mini-btn" onClick={addDistrictAll} disabled={!districtFilter}>
                <Plus size={14} /> İlçenin Tamamı
              </button>
            </div>
            <SearchableSelect
              label="Mahalle / Belde / Köy ekle"
              valueLabel=""
              placeholder={districtFilter ? (settlementLoading ? 'Yükleniyor...' : 'Yerleşim ara') : 'Önce ilçe seçin'}
              items={settlementOptions}
              onSelect={addSettlement}
              disabled={!districtFilter || settlementLoading}
              searchFields={['label', 'name', 'districtName', 'municipalityName']}
            />
            <div className="ckr-location-wide">
              <SelectionTags selections={multiSelections} onRemove={removeSelection} />
            </div>
          </>
        ) : (
          <>
            <SearchableSelect
              label="İlçe"
              valueLabel={district?.name}
              placeholder="İlçe seçin"
              items={districtOptions}
              onSelect={chooseDistrict}
              disabled={loading || !province}
              searchFields={['name']}
            />
            <SearchableSelect
              label="Mahalle / Belde / Köy"
              valueLabel={settlement?.label}
              placeholder={district ? (settlementLoading ? 'Yükleniyor...' : 'Yerleşim seçin') : 'Önce ilçe seçin'}
              items={settlementOptions}
              onSelect={setSettlement}
              disabled={!district || settlementLoading}
              searchFields={['label', 'name', 'municipalityName']}
            />
          </>
        )}
      </div>
    </div>
  );
}
